import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PageHeader, Field, NumberInput, Result, FormulaBox, Note } from "@/components/CalcUI";
import { fmt } from "@/lib/utils";
import { MEDICATIONS, getMedicationById } from "@/lib/medications";
import { useT } from "@/i18n/LanguageProvider";

export default function PediatricDosing() {
  const t = useT();
  const [searchParams] = useSearchParams();
  const initialMedId = useMemo(() => {
    const q = searchParams.get("drug");
    return q && MEDICATIONS.some((m) => m.id === q) ? q : MEDICATIONS[0].id;
  }, [searchParams]);
  const [medId, setMedId] = useState(initialMedId);
  const [weight, setWeight] = useState("");
  const [customMgPerKg, setCustomMgPerKg] = useState("");

  const med = getMedicationById(medId);
  const w = parseFloat(weight);

  const calc = useMemo(() => {
    if (!med || !w || w <= 0) return null;
    const mgkg = customMgPerKg ? parseFloat(customMgPerKg) : med.dose_mg_per_kg;
    if (!mgkg || mgkg <= 0) return null;

    const rawDose = mgkg * w;
    const capped = med.max_single_dose_mg ? Math.min(rawDose, med.max_single_dose_mg) : rawDose;
    const wasCapped = med.max_single_dose_mg && rawDose > med.max_single_dose_mg;

    const dailyMax = med.max_daily_mg_per_kg ? med.max_daily_mg_per_kg * w : null;
    const dailyMaxCapped =
      dailyMax !== null && med.max_daily_total_mg
        ? Math.min(dailyMax, med.max_daily_total_mg)
        : dailyMax ?? med.max_daily_total_mg;

    const volumeMl = med.volume_per_mg && capped ? capped * med.volume_per_mg : null;

    return { mgkg, rawDose, capped, wasCapped, dailyMaxCapped, volumeMl };
  }, [med, w, customMgPerKg]);

  return (
    <div data-testid="page-pediatric-dose">
      <PageHeader title={t("ped.title")} subtitle={t("ped.subtitle")} formula={t("ped.formula")} />

      <div className="px-6 md:px-10 py-8 grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl">
        <div className="space-y-6">
          <Field label={t("ped.medication")} testid="medication">
            <select
              value={medId}
              onChange={(e) => {
                setMedId(e.target.value);
                setCustomMgPerKg("");
              }}
              data-testid="medication-select"
              className="w-full bg-white border border-stone-200 rounded-sm px-3 py-2.5 text-base font-sans hover:border-stone-300 focus:border-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-700/30 transition-colors duration-100"
            >
              {MEDICATIONS.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name} — {m.route}
                  {m.in_development ? t("ped.dev_suffix") : ""}
                </option>
              ))}
            </select>
          </Field>

          <Field label={t("ped.weight")} unit="kg" testid="weight">
            <NumberInput
              value={weight}
              onChange={setWeight}
              placeholder={t("ped.weight.placeholder")}
              min={0}
              testid="weight-input"
            />
          </Field>

          <Field
            label={t("ped.custom", { default: med.dose_mg_per_kg })}
            unit="mg/kg"
            testid="custom-dose"
            hint={t("ped.custom.hint", {
              min: med.dose_range[0],
              max: med.dose_range[1],
              freq: med.frequency,
            })}
          >
            <NumberInput
              value={customMgPerKg}
              onChange={setCustomMgPerKg}
              placeholder={`${med.dose_mg_per_kg}`}
              min={0}
              testid="custom-dose-input"
            />
          </Field>

          <Note kind={calc?.wasCapped ? "warning" : "info"}>
            {calc?.wasCapped
              ? t("ped.capped", { raw: fmt(calc.rawDose, 1), max: med.max_single_dose_mg })
              : med.notes}
          </Note>

          {med.in_development && (
            <div
              className="border border-amber-700/40 bg-amber-50 text-amber-900 rounded-sm px-4 py-3 text-xs leading-relaxed flex items-start gap-2"
              data-testid="in-development-alert"
            >
              <span className="font-mono text-[10px] tracking-[0.12em] uppercase font-semibold bg-amber-700/10 px-1.5 py-0.5 rounded-sm shrink-0 mt-0.5">
                {t("common.dev_badge")}
              </span>
              <span>
                <span className="font-semibold">{t("common.in_development.long")}</span>{" "}
                {t("ped.dev_alert")}
              </span>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <Result
            label={t("ped.result.single")}
            value={calc ? fmt(calc.capped, 1) : "—"}
            unit="mg"
            big
            testid="result-single-dose"
            accent={calc?.wasCapped ? "danger" : "default"}
          />
          {calc?.volumeMl !== null && calc?.volumeMl !== undefined && (
            <Result
              label={med.custom_volume_label || t("ped.result.volume")}
              value={fmt(calc.volumeMl, 2)}
              unit="ml"
              testid="result-volume"
            />
          )}
          <Result
            label={t("ped.result.frequency")}
            value={med.frequency}
            testid="result-frequency"
          />
          {calc?.dailyMaxCapped && (
            <Result
              label={t("ped.result.daily_max")}
              value={fmt(calc.dailyMaxCapped, 0)}
              unit="mg / 24 h"
              testid="result-daily-max"
            />
          )}
          <FormulaBox testid="breakdown-box">
            {calc
              ? `${fmt(w, 2)} kg × ${calc.mgkg} mg/kg = ${fmt(calc.rawDose, 2)} mg${
                  calc.wasCapped ? `  →  capped at ${med.max_single_dose_mg} mg` : ""
                }\n${t("ped.breakdown.route")}: ${med.route} · ${med.frequency}`
              : t("ped.breakdown.empty")}
          </FormulaBox>
        </div>
      </div>
    </div>
  );
}
